import React, { useState } from 'react'

import { useAuthenticationActions } from 'redux/actions/Authentication'

import { SxProps } from '@mui/material'

import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import GenericListItem from 'components_new/molecules/GenericListItem'
import ListItemButton from 'components_new/atoms/List/ListItemButton'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Menu from 'components_new/atoms/Menu'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Tooltip from 'components_new/atoms/Tooltip'

import EditProfileDialog from './dialogs/EditProfileDialog'

import Loading from './loading'

import { useTranslation } from 'language/translator'

import { NAVBAR_COLLAPSED_TOOLTIP_OFFSET } from '../Navbar/lib'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useNavigation } from 'redux/hooks/NavigationMenuStore'

interface ProfileAvatarProps {
  sx?: SxProps
}

const ProfileAvatar = React.forwardRef((props: ProfileAvatarProps, ref) => {
  const { sx } = props

  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false)
  const { t } = useTranslation()
  const { account, loading: accountLoading } = useAccountSettings()
  const { signOut } = useAuthenticationActions()

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLAnchorElement | null>(
    null
  )
  const menuOpen = Boolean(menuAnchorEl)

  const handleClick = (event: any) => {
    setMenuAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setMenuAnchorEl(null)
  }

  const { open } = useNavigation()

  /*-- loading --*/
  if (accountLoading) {
    return <Loading />
  }

  /*-- default --*/
  return (
    <>
      <Tooltip
        slotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
        title={
          !open ? (
            <>
              <b>
                {account.first_name} {account.last_name}
              </b>
              <br />
              {account.email}
            </>
          ) : undefined
        }
        placement={'right'}
      >
        <ListItemButton
          dense={true}
          onClick={handleClick}
          ref={ref}
          sx={{
            height: 48,
            borderRadius: 1,
            px: '5px',
            display: 'flex',
            alignItems: 'center',
            flex: 1, // Ensure it takes up available space
            overflow: 'hidden', // Prevent overflow
            ...sx
          }}
        >
          <GenericListItem
            body={account.email}
            disablePadding={true}
            title={`${account.first_name} ${account.last_name}`}
            variant="user"
            sx={{
              ['& .ListItemIcon']: {
                marginLeft: open ? '0px' : '-3px',
                transition: 'margin-left 0.5s ease'
              },
              ['& .ListItemText']: {
                opacity: open ? 1 : 0,
                transition: 'opacity 0.5s ease'
              }
            }}
          />
        </ListItemButton>
      </Tooltip>

      {/*-- menu --*/}
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={handleClose}
        open={menuOpen}
        disableScrollLock
        PaperProps={{
          sx: {
            width: 300
          }
        }}
      >
        <MenuItem
          onClick={() => {
            setEditProfileDialogOpen(true)
            handleClose()
          }}
        >
          <ListItemIcon>
            <Icon name="Settings" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('global', 'settings')} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => signOut()}>
          <ListItemIcon>
            <Icon name="LogoutOutlined" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('global', 'sign_out')} />
        </MenuItem>
      </Menu>

      {/*-- dialogs --*/}
      <EditProfileDialog
        open={editProfileDialogOpen}
        onClose={() => setEditProfileDialogOpen(false)}
      />
    </>
  )
})

export default ProfileAvatar
